import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { fmtGHS, fmtDate, fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { StatusBadge } from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Printer, Pencil, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import { CreditBar, orderBadge, TypeTag } from "./common";
import { printSummary } from "./summary";

const KIND = { initial: "Initial", adjust: "Adjusted", new_period: "New period" };

const AllocationForm = ({ h, onDone }) => {
  const [v, setV] = useState(String(h.allocation));
  const [reason, setReason] = useState("");
  const [np, setNp] = useState(false);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (reason.trim().length < 3) return toast.error("Give a reason for the change");
    setBusy(true);
    try {
      await api.post(`/wholesale/hospitals/${h.id}/allocation`, { allocation: Number(v), reason, new_period: np });
      toast.success(np ? "New allocation period started" : "Allocation updated"); setReason(""); setNp(false); onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-border p-3 space-y-2">
      <div className="text-sm font-medium flex items-center gap-1.5"><SlidersHorizontal className="h-4 w-4" /> Change approved allocation</div>
      <div className="grid grid-cols-1 sm:grid-cols-[10rem_1fr_auto] gap-2 items-end">
        <div><Label className="text-xs">New allocation (GHS)</Label><Input type="number" min="0" step="0.01" value={v} onChange={(e) => setV(e.target.value)} data-testid="ws-alloc-amount" /></div>
        <div><Label className="text-xs">Reason</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Q4 approval from board" data-testid="ws-alloc-reason" /></div>
        <Button disabled={busy} onClick={save} data-testid="ws-alloc-save-btn">Save</Button>
      </div>
      <div className="flex items-center gap-2 text-xs"><Switch checked={np} onCheckedChange={setNp} data-testid="ws-alloc-new-period" />
        <span>Start a new allocation period <span className="text-muted-foreground">(usage restarts from zero; the current period is archived)</span></span></div>
    </div>
  );
};

export const HospitalDetailDialog = ({ id, can, from, to, onClose, onChanged, onEdit }) => {
  const [h, setH] = useState(null);
  const load = () => api.get(`/wholesale/hospitals/${id}`).then((r) => setH(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { setH(null); if (id) load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  const summary = async () => {
    try { const { data } = await api.get("/wholesale/allocation-summary", { params: { hospital_id: id, date_from: from, date_to: to } }); printSummary(data); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Dialog open={!!id} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="ws-hospital-detail">
        {!h ? <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div> : (<>
          <DialogHeader>
            <DialogTitle>{h.name}</DialogTitle>
            <DialogDescription>{[h.contact_person, h.phone, h.email, h.address].filter(Boolean).join(" · ") || "No contact details"} · current period since {fmtDate(h.period_start)}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
            {[["Approved allocation", h.allocation, "allocation"], ["Used", h.used, "used"], ["Committed", h.committed, "committed"], ["Remaining", h.remaining, "remaining"]].map(([l, v, k]) => (
              <div key={k} className="rounded-lg border border-border p-3"><div className="text-xs text-muted-foreground">{l}</div><div className="font-mono font-semibold" data-testid={`ws-detail-${k}`}>{fmtGHS(v)}</div></div>
            ))}
          </div>
          <div className="flex items-center gap-2"><CreditBar pct={h.utilisation_pct} /><span className="text-xs font-mono whitespace-nowrap">{h.utilisation_pct}% used</span></div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={summary} data-testid="ws-print-summary-btn"><Printer className="h-4 w-4 mr-1.5" /> Allocation usage summary ({from} – {to})</Button>
            {can("wholesale.accounts") && <Button variant="outline" onClick={() => onEdit(h)} data-testid="ws-edit-hospital-btn"><Pencil className="h-4 w-4 mr-1.5" /> Edit profile</Button>}
          </div>
          {can("wholesale.accounts") && <AllocationForm h={h} onDone={() => { load(); onChanged(); }} />}
          <div className="grid sm:grid-cols-2 gap-4">
            <div><div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Recent supply orders</div>
              {h.orders.slice(0, 10).map((o) => <div key={o.id} className="flex justify-between items-center text-sm py-1 border-b border-border/50"><span className="font-mono text-xs flex items-center gap-1.5">{o.code} <TypeTag t={o.fulfilment_type} /> <StatusBadge value={orderBadge(o.status)} label={o.status} /></span><span className="font-mono">{fmtGHS(o.total_cost)}</span></div>)}
              {!h.orders.length && <div className="text-xs text-muted-foreground">None</div>}</div>
            <div data-testid="ws-alloc-log"><div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Allocation history</div>
              {h.allocation_log.map((x) => (
                <div key={x.id} className="text-xs py-1 border-b border-border/50">
                  <div className="flex justify-between"><span>{fmtDateTime(x.at)} · {x.by}</span><span className="font-mono">{fmtGHS(x.old)} → {fmtGHS(x.new)}</span></div>
                  <div className="text-muted-foreground">{KIND[x.kind] || x.kind}: {x.reason}</div>
                </div>
              ))}
              {!h.allocation_log.length && <div className="text-xs text-muted-foreground">No changes logged yet</div>}
              {h.periods.length > 0 && <div className="mt-2 text-xs text-muted-foreground">Past periods: {h.periods.map((p) => `${fmtDate(p.start)}–${fmtDate(p.end)}: ${fmtGHS(p.used)} of ${fmtGHS(p.allocation)}`).join(" · ")}</div>}
            </div>
          </div>
        </>)}
      </DialogContent>
    </Dialog>
  );
};
